import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Loader2, ShoppingCart, ChevronRight, CheckCircle2,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Pretty flag emojis keyed by country slug (lowercase 2-letter code from
// channel_type — amazon_in → "in"). Kept small; anything unmapped falls
// back to a neutral globe.
const FLAG = {
  in: '🇮🇳', ae: '🇦🇪', sa: '🇸🇦', eg: '🇪🇬', tr: '🇹🇷',
  uk: '🇬🇧', de: '🇩🇪', fr: '🇫🇷', it: '🇮🇹', es: '🇪🇸', nl: '🇳🇱',
  us: '🇺🇸', ca: '🇨🇦', mx: '🇲🇽', jp: '🇯🇵', au: '🇦🇺', sg: '🇸🇬',
};

const REGIONS = [
  { key: 'eu', label: 'Europe, India & Middle East' },
  { key: 'na', label: 'North America' },
  { key: 'fe', label: 'Far East & Oceania' },
];

function regionFor(host) {
  if (host?.includes('-na.')) return 'na';
  if (host?.includes('-fe.')) return 'fe';
  return 'eu';
}

export default function AmazonMarketplacesHub() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');
  const headers = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, accRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/integrations/amazon/config`, headers()),
        axios.get(`${API_BASE_URL}/integrations/amazon/accounts`, headers()),
      ]);
      setConfig(cfgRes.data);
      setAccounts(accRes.data?.accounts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Amazon config');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  const countriesByRegion = {};
  (config?.countries || []).forEach((c) => {
    const r = regionFor(c.host);
    countriesByRegion[r] = countriesByRegion[r] || [];
    countriesByRegion[r].push(c);
  });
  const countsByType = {};
  accounts.forEach((a) => {
    countsByType[a.channel_type] = (countsByType[a.channel_type] || 0) + 1;
  });

  return (
    <div className="min-h-screen bg-wt-bg">
      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/integrations')}
          className="mb-4 text-wt-text-muted hover:text-wt-text-primary"
          data-testid="amazon-hub-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Integrations
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#232F3E] to-[#FF9900] flex items-center justify-center text-white shadow-lg">
            <ShoppingCart className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-wt-text-primary">
              Amazon Marketplaces
            </h1>
            <p className="text-sm text-wt-text-muted">
              {config?.countries?.length || 0} countries supported • {accounts.length} account{accounts.length === 1 ? '' : 's'} connected
            </p>
          </div>
        </div>

        {config?.oauth_enabled && (
          <Card className="p-4 mb-6 border-emerald-500/30 bg-emerald-500/5">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-emerald-300">Public "Connect with Amazon" enabled</p>
                <p className="text-xs text-emerald-200/80 mt-1">
                  Sellers can now self-onboard their Amazon account with one click on any country page
                  below — no manual refresh token copy-paste needed.
                </p>
              </div>
            </div>
          </Card>
        )}

        {REGIONS.map((r) => {
          const list = countriesByRegion[r.key] || [];
          if (list.length === 0) return null;
          return (
            <section key={r.key} className="mb-8" data-testid={`amazon-region-${r.key}`}>
              <h2 className="text-sm font-medium text-wt-text-muted uppercase tracking-wide mb-3">
                {r.label}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {list.map((c) => {
                  const slug = c.channel_type.split('_')[1];
                  const count = countsByType[c.channel_type] || 0;
                  return (
                    <Card
                      key={c.channel_type}
                      className="p-4 cursor-pointer hover:border-wt-gold transition group"
                      onClick={() => navigate(`/integrations/amazon/${slug}`)}
                      data-testid={`amazon-country-card-${slug}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">{FLAG[slug] || '🌐'}</span>
                            <h3 className="text-sm font-medium text-wt-text-primary truncate">
                              {c.label.replace('Amazon ', '')}
                            </h3>
                          </div>
                          <p className="text-[11px] text-wt-text-muted">
                            {c.currency} • {c.marketplace_id.slice(0, 10)}…
                          </p>
                          {count > 0 && (
                            <Badge
                              variant="outline"
                              className="mt-2 bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[11px]"
                            >
                              {count} connected
                            </Badge>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-wt-text-muted group-hover:text-wt-gold shrink-0" />
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
